// src/modules/app-views/crm/sticksService.js
// *******************************************************************************
// STICKS SERVICE
// *******************************************************************************
// Service layer for sticky notes CRUD operations (mural board)
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// API METHODS
// *******************************************************************************

/**
 * Fetch all sticks for the current user
 * @returns {Promise<Array>} Array of sticks
 */
async function fetchAll() {
  const [data] = await axiosService.get({
    url: API.STICKS.ALL,
    returnAsObject: false,
  });

  return data || [];
}

/**
 * Create or update a stick (upsert)
 * @param {Object} data - Stick data
 * @param {string} [data._id] - Stick ID (if updating)
 * @returns {Promise<Object>} Created/updated stick
 */
async function save(data) {
  const _id = data?._id;

  if (_id) {
    // Update existing
    const url = API.STICKS.URL.replace(':_id?', _id);
    const [response] = await axiosService.put({
      url,
      data,
      returnAsObject: true,
    });
    return response;
  }

  // Create new
  const url = API.STICKS.URL.replace(':_id?', '');
  const [response] = await axiosService.post({
    url,
    data,
    returnAsObject: true,
  });
  return response;
}

/**
 * Delete a stick
 * @param {string} _id - Stick ID
 * @returns {Promise<Object>} Delete result
 */
async function remove(_id) {
  const url = API.STICKS.URL.replace(':_id?', _id);
  const [response] = await axiosService.delete({
    url,
    returnAsObject: true,
  });
  return response;
}

/**
 * Reorder sticks (bulk update order field)
 * @param {Array<{_id: string, order: number}>} items - Array of stick IDs with new order
 * @returns {Promise<Object>} Bulk update result
 */
async function reorder(items) {
  const [response] = await axiosService.put({
    url: API.STICKS.REORDER,
    data: { items },
    returnAsObject: true,
  });
  return response;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
const sticksService = {
  fetchAll,
  save,
  remove,
  reorder,
};

export default sticksService;
